


import { useEffect, useState } from 'react';
import { Download, UtensilsCrossed } from 'lucide-react';
import Layout from 'components/ui/Layout';
import { useMenus } from 'features/menu/hooks/useMenus';
import ViewMenuModal from 'features/menu/components/ViewMenuModal';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';
import { menuServices } from 'features/menu/services/menu.service';


const MenuManagement = () => {

  // -----------------------------------
  // FILTERS
  // -----------------------------------

  const initialFilters = {
    page:         '1',
    limit:        '10',
    search:       '',
    sellerStatus: 'all',
    serviceType:  'all',
    foodType:     'all',
    sortBy:       'createdAt',
    sortOrder:    'desc',
  };

  // -----------------------------------
  // STATE
  // -----------------------------------

  const [filters, setFilters]           = useState(initialFilters);
  const [searchInput, setSearchInput]   = useState('');
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [isViewOpen, setIsViewOpen]     = useState(false);

  // -----------------------------------
  // API
  // -----------------------------------

  const [{ data, isLoading, isError, error }] = useMenus(filters);

  const { data: profileData } = useUserProfile();
        const user = profileData?.data;
        
        const restrictedRoles = [
          "category_manager",
          "travel_manager",
          "operations_manager",
          "finance_manager",
        ];
        
        const showPermissionError = restrictedRoles.includes(
          user?.role?.name
        );




   const handleExport =
  async () => {
    try {

      const response =
        await menuServices.exportMenus(
          filters
        );

      const blob =
        new Blob(
          [response.data],
          {
            type: 'text/csv',
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          'a'
        );

      link.href = url;

      link.download =
        `menus-${Date.now()}.csv`;

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      window.URL.revokeObjectURL(
        url
      );

    } catch (error) {

      console.error(
        'Export failed',
        error
      );

    }
  };

  // -----------------------------------
  // SEARCH DEBOUNCE
  // -----------------------------------

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
        page:   '1',
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // -----------------------------------
  // LOADING
  // -----------------------------------

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          Loading...
        </div>
      </Layout>
    );
  }

  // -----------------------------------
  // ERROR
  // -----------------------------------

  if (isError) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen text-red-500">
          {error?.message || 'Something went wrong'}
        </div>
      </Layout>
    );
  }

  // -----------------------------------

  return (
    <Layout>
      <div className="p-4">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <UtensilsCrossed size={32} className="text-primary" />
              Menu Management
            </h1>
            <p className="text-muted-foreground">Manage food menus</p>
          </div>
          <p className="text-sm text-muted-foreground">
            Total Results: {data?.pagination?.totalItems}
          </p>

           {!showPermissionError && (
  <button
    onClick={handleExport}
    className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
  >
    <Download size={16} />
    <span>Export</span>
  </button>
)}
        </div>

        {/* FILTERS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">

          {/* SEARCH */}
          <input
            type="text"
            placeholder="Search menus..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm"
          />

          {/* SELLER STATUS */}
          <select
            value={filters.sellerStatus}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                sellerStatus: e.target.value,
                page: '1',
              }))
            }
            className="border rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="all">All Seller Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="blocked">Blocked</option>
          </select>

          {/* SERVICE TYPE */}
          {/* <select
            value={filters.serviceType}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                serviceType: e.target.value,
                page: '1',
              }))
            }
            className="border rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="all">All Services</option>
            <option value="delivery">Delivery</option>
            <option value="pickup">Pickup</option>
            <option value="dine_in">Dine In</option>
          </select> */}

          {/* FOOD TYPE */}
          <select
            value={filters.foodType}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                foodType: e.target.value,
                page: '1',
              }))
            }
            className="border rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="all">All Food Types</option>
            <option value="veg">Veg</option>
            <option value="non_veg">Non Veg</option>
            <option value="egg">Egg</option>
          </select>

          {/* LIMIT */}
          <select
            value={filters.limit}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                limit: e.target.value,
                page: '1',
              }))
            }
            className="border rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
          </select>

        </div>

        {/* EMPTY STATE */}
        {data?.data?.length === 0 && (
          <div className="text-center py-20 text-muted-foreground">
            No menus found
          </div>
        )}

        {/* SELLERS */}
        <div className="space-y-4">
          {

            showPermissionError ? (
    <tr>
      <td
        colSpan='11'
        className='px-4 py-8 text-center text-destructive'
      >
        Error loading Menu: You do not have permission to perform this action!
      </td>
    </tr>
  ) : data?.data?.map((seller) => (
            <div
              key={seller.sellerId}
              className="border rounded-lg p-4 bg-card"
            >

              {/* SELLER HEADER */}
              <div className="flex items-center justify-between">

                {/* LEFT */}
                <div className="flex items-center gap-3">
                  <img
                    src={seller.profilePhoto || '/placeholder-user.png'}
                    alt="seller"
                    className="w-11 h-11 rounded-full object-cover"
                  />

                  <div>
                    <h3 className="font-semibold text-base">
                      {seller.firstName || 'N/A'} {seller.lastName || ''}
                    </h3>

                      <h2 className="font-semibold text-base">
                      {seller.restaurantName || 'N/A'}
                    </h2>

                    
                    <p className="text-xs text-muted-foreground">
                      {seller.email || 'No Email'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {seller.phoneNumber || 'No phone number available'}
                    </p>
                    {/* <p className="text-xs capitalize">
                      {seller.sellerType || 'Unknown'}
                    </p> */}
                    <p className="text-xs mt-1">
                      <span
                        className={`px-2 py-1 rounded-full ${
                          seller.sellerStatus === 'active'
                            ? 'bg-green-500/10 text-green-600'
                            : seller.sellerStatus === 'pending_approval'
                            ? 'bg-yellow-500/10 text-yellow-600'
                            : 'bg-red-500/10 text-red-600'
                        }`}
                      >
                        {seller.sellerStatus || 'Unknown'}
                      </span>
                    </p>
                  </div>
                </div>

                {/* RIGHT */}
                {/* <p className="text-sm font-medium">
                  Menus: {seller.totalMenus}
                </p> */}

              </div>

              {/* MENUS */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {seller.menus?.map((menu) => (
                  <div
                    key={menu.menuId}
                    className="border rounded-lg p-3 bg-background"
                  >

                    {/* MENU COVER IMAGE                          */}
                    {/* key = signed S3 URL (transformer output)  */}
                    {/* url = original stored value (may be stale)*/}
                    {/* <img
                      src={
                        menu?.images?.[0]?.key ||
                        menu?.images?.[0]?.url ||
                        '/placeholder-food.png'
                      }
                      alt="menu"
                      className="w-full h-32 rounded-md object-cover"
                    /> */}

                    {/* DETAILS */}
                    <div className="mt-2 space-y-1">
                      {/* <p className="text-xs">
                        <span className="font-medium">Service:</span>{' '}
                        {menu.serviceType}
                      </p> */}
                      <p className="text-xs">
                        <span className="font-medium">Subcategories:</span>{' '}
                        {menu.totalCategories}
                      </p>
                      <p className="text-xs">
                        <span className="font-medium">Items:</span>{' '}
                        {menu.totalItems}
                      </p>
                      {/* <p className="text-xs">
                        <span className="font-medium">Status:</span>{' '}
                        <span className={menu.isActive ? 'text-green-600' : 'text-red-500'}>
                          {menu.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </p> */}
                    </div>

                    {/* VIEW BUTTON */}
                    <button
                      onClick={() => {
                        setSelectedMenu(menu);
                        setIsViewOpen(true);
                      }}
                      className="w-full mt-3 px-3 py-1.5 text-sm rounded-lg bg-primary text-white hover:opacity-90 transition"
                    >
                      View Menu
                    </button>

                  </div>
                ))}
              </div>

            </div>
          ))}
        </div>

        {/* PAGINATION */}
        {data?.pagination?.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">

            <button
              disabled={filters.page === '1'}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  page: String(Number(prev.page) - 1),
                }))
              }
              className="px-4 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-muted transition"
            >
              Previous
            </button>

            <span className="text-sm text-muted-foreground">
              Page {filters.page} of {data?.pagination?.totalPages}
            </span>

            <button
              disabled={Number(filters.page) >= data?.pagination?.totalPages}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  page: String(Number(prev.page) + 1),
                }))
              }
              className="px-4 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-muted transition"
            >
              Next
            </button>

          </div>
        )}

      </div>

      {/* VIEW MODAL */}
      <ViewMenuModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        menu={selectedMenu}
      />

    </Layout>
  );
};

export default MenuManagement;